/**
 * The day's record, for the "Today" view.
 *
 * A different question from the board's, and asked on a different clock. The
 * board is the live queue: everything still needing attention, refetched on every
 * realtime signal. This is the shop's own calendar day of closed orders — the
 * same day `restaurant_dashboard` buckets takings by — which is a record rather
 * than a queue, so it is read when it is shown and paged through rather than
 * fetched whole on every board reload.
 *
 * Search is the server's, not the screen's. Filtering the loaded page would
 * search the page rather than the day, so a code from this morning would read as
 * "not found" until enough pages were loaded to reach it — which is why the
 * query carries the search and the count of what matched.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Order } from '@yummy/shared';
import { fetchDayOrders } from '@yummy/db';

import { supabase } from '../lib/supabase';
import { useMerchant } from './auth';

/** How many orders a page of the record holds. */
const PAGE_SIZE = 50;

/** How long typing settles before the day is re-read. */
const SEARCH_SETTLE_MS = 250;

/** What the record view needs to draw itself. */
export interface DayOrdersState {
  /** The pages loaded so far, newest first. */
  orders: Order[];
  /** How many closed orders the day holds, matching the search. */
  total: number;
  /** True while the first page is being read. */
  loading: boolean;
  /** True while a further page is being read. */
  loadingMore: boolean;
  error: string | null;
  /** Whether the day holds more than has been loaded. */
  more: boolean;
  /** Reads the next page of the same search. */
  loadMore: () => void;
  /** Re-reads from the first page. */
  reload: () => void;
}

/**
 * Reads the day's closed orders, a page at a time.
 *
 * @param active Whether the record is on screen. Nothing is fetched while it is
 * not: a view that is usually closed should not pay for a query on every board
 * reload.
 * @param search What the counter typed, applied to the whole day by the server.
 * @returns The loaded pages, the day's count, and the controls to go further.
 */
export function useDayOrders(active: boolean, search: string): DayOrdersState {
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;

  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The search the loaded pages belong to.
   *
   * Separate from the field's value so a request is sent for the term that
   * settled rather than for every keystroke, and so "Load more" continues the
   * question the pages already answer rather than mixing two.
   */
  const [settled, setSettled] = useState(search);

  // A slow page from an abandoned search must not land on top of a newer one.
  const generation = useRef(0);
  // How many rows are loaded, for the next page's offset. A ref rather than
  // reading `orders` inside the callback, which would make the callback a new
  // function on every page and re-trigger the opening fetch.
  const loaded = useRef(0);
  useEffect(() => {
    loaded.current = orders.length;
  }, [orders.length]);

  useEffect(() => {
    if (search === settled) return;
    const id = setTimeout(() => setSettled(search), SEARCH_SETTLE_MS);
    return () => clearTimeout(id);
  }, [search, settled]);

  const read = useCallback(
    async (offset: number, append: boolean) => {
      if (!restaurantId) return;
      const current = ++generation.current;
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const page = await fetchDayOrders(supabase, restaurantId, {
          limit: PAGE_SIZE,
          offset,
          search: settled,
        });
        if (current !== generation.current) return;
        setOrders((existing) => (append ? [...existing, ...page.orders] : page.orders));
        setTotal(page.total);
        setError(null);
      } catch (cause) {
        if (current !== generation.current) return;
        setError(cause instanceof Error ? cause.message : String(cause));
      } finally {
        if (current === generation.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [restaurantId, settled]
  );

  // The first page, read when the view opens and re-read when the question
  // changes. Reopening is deliberate: a hand-over made on the board in between
  // belongs in the record by the time it is looked at.
  useEffect(() => {
    if (!active) return;
    void read(0, false);
  }, [active, read]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore) return;
    void read(loaded.current, true);
  }, [read, loading, loadingMore]);

  const reload = useCallback(() => {
    void read(0, false);
  }, [read]);

  return {
    orders,
    total,
    loading,
    loadingMore,
    error,
    more: orders.length < total,
    loadMore,
    reload,
  };
}
